import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { CdkCell, CdkCellDef, CdkColumnDef, CdkHeaderCell, CdkHeaderCellDef } from '@angular/cdk/table';
import { NgTemplateOutlet } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    TrackByFunction,
    ViewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIcon, MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { environment } from '@ecs-environment';
import { mediaQueries, tracking } from '@ecs-helpers';
import { Basket, BasketItem, BasketItemType, BasketService } from '@ecs-services/basket.service';
import { ModalService } from '@ecs-services/modal/modal.service';
import { OverlayService } from '@ecs-services/overlay/overlay.service';
import { isSerialNumberResult, isSparePartListResult } from '@ecs-services/search/search.helper';
import { SearchResult } from '@ecs-services/search/search.interface';
import { SparePartService } from '@ecs-services/spare-part/spare-part.service';
import { WishlistRequest, WishlistService } from '@ecs-services/wish-list.service';
import { TranslateDirective, TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
    BaseTableComponent,
    BaseTableModule,
    CategoryHeaderRow,
    CategoryItemRow,
    DefaultTooltipComponent,
    downloadCsvFile,
    hasRowData,
    HeaderConfigService,
    InputRow,
    isCategoryItemRow,
    isContainedRow,
    isExpandableRow,
    isFullRowContent,
    isGroupHeaderRow,
    isSelectableRow,
    isStandardRow,
    NgUseDirective,
    NumbersOnlyDirective,
    OnDestroyBase,
    SelectableRow,
    SpecialRow,
    TableFilterService,
    TableSortService,
    TooltipDirective,
    TrackClickDirective,
    TrackingService,
    transformObjectsToCsv,
    WINDOW,
} from '@os-components';
import {
    SewButton,
    SewDialog,
    SewIcon,
    SewLink,
    SewLoaderMini,
    SewRadio,
    SewRadioGroup,
    SewSearchField,
    SewTableInput,
    SewWsButton,
} from '@sew/ui-components-angular/standalone';
import { forkJoin, Observable } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import {
    isSelectableSparePartRowArray,
    isSparePart,
    isSparePartGroupList,
} from '../../features/result/spare-parts-result/spare-parts.helper';
import {
    SparePart,
    SparePartGroup,
    SparePartGroupList,
    SparePartGroups,
    SpareParts,
    SparePartsCollection,
} from '../../features/result/spare-parts-result/spare-parts.interface';
import { endLoading, startLoading } from '../../helpers/loading/loading';
import { ConvertLinebreakToHtmlPipe } from '../../pipes/convert-linebreak-to-html/convert-linebreak-to-html.pipe';
import { CableConfigurationComponent } from '../dialogs/part-configuration-wizard/components/cable-configuration/cable-configuration.component';
import { ECoverConfigurationComponent } from '../dialogs/part-configuration-wizard/components/e-cover-configuration/e-cover-configuration.component';
import { GearOilConfigurationComponent } from '../dialogs/part-configuration-wizard/components/gear-oil-configuration/gear-oil-configuration.component';
import {
    DialogComponent,
    PartConfigurationWizardComponent,
    StepType,
    WizardStep,
    XSellingWizardOutput,
} from '../dialogs/part-configuration-wizard/part-configuration-wizard.component';
import { isCrossSellingItem } from '../dialogs/part-configuration-wizard/part-configuration-wizard.helper';
import { GroupToggleComponent } from '../group-toggle/group-toggle.component';
import { CustomTab } from '../group-toggle/group-toggle.interface';
import { PartTableSkeletonComponent } from '../part-table-skeleton/part-table-skeleton.component';
import { RecommendedPart } from '../parts-recommendation/parts-recommendation.interface';
import { AvailabilityCellComponent } from './components/availability-cell/availability-cell.component';
import { DetailType } from './components/part-detail/part-detail-types.enum';
import { PartDetailComponent } from './components/part-detail/part-detail.component';
import { PartDetailService } from './components/part-detail/part-detail.service';
import {
    eCoverNeedsConfiguration,
    getItemNumber,
    getUniqueGroupId,
    getUniquePartId,
    isCableCrossSellingItemRow,
    isECoverCrossSellingItemRow,
    isGearOilCrossSellingItemRow,
    isPartRowIdAndItemNumber,
    removeSpecificColumnsIfSEWParterSite,
} from './part-table.helper';
import { PartDetail, PartGroupRow, PartInput, PartRow } from './part-table.interface';

export enum ReductionLevel {
    NONE = 0,
    FIRST = 1,
    SECOND = 2,
    THIRD = 3,
}

export enum CSVExportType {
    INTERNAL = 'internal',
    EXTERNAL = 'external',
}

export type PartAmounts = { [partId: string]: number };

const GLOBAL_SEARCH_TAG_POSITION = 0;
const GLOBAL_SEARCH_FIELDS: ReadonlyArray<keyof SparePart & string> = ['itemNumber', 'name', 'id'];
const GLOBAL_SEARCH_LABEL = 'Search';

@Component({
    selector: 'ecs-part-table',
    templateUrl: './part-table.component.html',
    styleUrls: ['./part-table.component.scss'],
    // provide filter and sort service in component so each table has its own services
    providers: [TableFilterService, TableSortService],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        GroupToggleComponent,
        BaseTableModule,
        CdkColumnDef,
        CdkHeaderCellDef,
        CdkHeaderCell,
        CdkCellDef,
        CdkCell,
        TooltipDirective,
        ReactiveFormsModule,
        NumbersOnlyDirective,
        FormsModule,
        NgUseDirective,
        AvailabilityCellComponent,
        NgTemplateOutlet,
        TrackClickDirective,
        TranslateDirective,
        PartTableSkeletonComponent,
        TranslatePipe,
        ConvertLinebreakToHtmlPipe,
        SewIcon,
        SewButton,
        SewTableInput,
        SewLoaderMini,
        SewLink,
        SewDialog,
        SewRadioGroup,
        SewRadio,
        SewWsButton,
        DefaultTooltipComponent,
        SewSearchField,
        MatIcon,
    ],
})
export class PartTableComponent extends OnDestroyBase implements OnInit, OnDestroy {
    @Input() public set items(items: SparePartsCollection | SparePartGroupList | null) {
        if (items === null) {
            return;
        }

        this.isGroupList = isSparePartGroupList(items);
        this.listItems = isSparePartGroupList(items)
            ? items.groups
            : this.createDummyGroupList(items.spareParts);

        /**
         * For sparePartResults the id is a unique identifier for a row.
         * For serialNumberResults and sparePartListResult a unique row is identified by the combination of driveGroup, itemNumber and id.
         * See {@link updateGroupRows} for usage
         *
         * @see issue #446175
         */
        const compareByIdAndIndex =
            items.hasOwnProperty('type') &&
            (isSerialNumberResult(items as SearchResult) || isSparePartListResult(items as SearchResult));
        const isUpdate = this.areGroupRowsAndSparePartsConsistent(this.previousGroups, this.listItems);

        this.columns = removeSpecificColumnsIfSEWParterSite(
            this.headerconfigService.getCountryShort(),
            this.getDisplayColumns(this.isGroupList, this.listItems)
        );
        this.groupHeaderColumns = this.getDisplayGroupHeaderColumns();
        this.partGroups = this.createGroupRows(this.listItems, isUpdate, compareByIdAndIndex);
        this.partAmounts = this.setupPartAmounts(this.listItems);
        this.checkRowsNeeded = false;
        setTimeout(() => (this.checkRowsNeeded = true));
    }

    @Input() public searchTerm?: string;
    /**
     * Serialnumber or Spare Part List Number
     */
    @Input() public identificationNumber?: string;
    @Input() public userStatus?: { isUserLoggedIn: boolean; isUserEmployee: boolean };
    @Input() public showPartGroupHeaderAsSkeleton = false;
    /**
     * Turns group headers into collapsable categories. See type: CategoryHeaderRow. For example: used for cabinet inverters
     */
    @Input() public collapsableGroupHeaders = false;

    @Input() public isAtex = false;
    @Input() public isDtcKit?: boolean;
    @Input() public isIndustrialGear = false;
    @Input() public isCabinetInverter = false;
    @Input() public isDesktopRecommendationAccordionExpanded = false;
    @Input() public isRecommendationAccordionAvailable = false;
    @Output() public rowMarked = new EventEmitter<SelectableRow<SparePart> | null>();
    @Output() public showInDrawing = new EventEmitter<void>();

    public partGroups?: readonly PartGroupRow[];
    public columns: string[] | null = null;
    public groupHeaderColumns: string[] | null = null;
    public partAmounts: PartAmounts = {};
    public checkRowsNeeded = true;
    public markedRow$!: Observable<PartRow | null>;
    public loginUrl!: string;
    // customTabs for inverter
    public customTabs: readonly CustomTab[] = [
        { index: 0, textKey: 'GROUP_TOGGLE.SUITABLE_ACCS', selected: true },
    ];

    public ReductionLevel = ReductionLevel;
    public contentReductionLevel: ReductionLevel = ReductionLevel.NONE;
    public getUniqueGroupIdFunction = getUniqueGroupId;

    public isExportCSVDialogVisible = false;
    public exportCSVType: CSVExportType = CSVExportType.EXTERNAL;
    public CSVExportType = CSVExportType;
    public tableSearchTerm = '';

    @ViewChild(BaseTableComponent) private table!: BaseTableComponent<SparePart, SparePartGroup, PartDetail>;
    @ViewChild('basketButton', { read: ElementRef })
    private basketButtonRef!: ElementRef<HTMLSewButtonElement>;
    @ViewChild('wishlistButton', { read: ElementRef })
    private wishlistButtonRef!: ElementRef<HTMLSewButtonElement>;

    @ViewChild('button', { read: ElementRef })
    private buttonRef!: ElementRef<HTMLSewButtonElement>;

    private isGroupList = false;
    private listItems!: SparePartGroups;
    private checkedRows: SelectableRow<SparePart>[] = [];
    private previousGroups: SparePartGroups = [];
    private previousPartGroupRows: PartGroupRow[] = [];
    public isRecommendationActive = false;
    public matIconName = 'light-bulb';
    public matIconName2 = 'light-bulb-white';
    public selectedRowId: string = '';
    public isMobile = false;

    public constructor(
        private basketService: BasketService,
        private partDetailService: PartDetailService,
        private overlayService: OverlayService,
        @Inject(WINDOW) private window: Window,
        private filterService: TableFilterService<SparePart, SparePartGroup, PartDetail>,
        private sortService: TableSortService<SparePart, SparePartGroup, PartDetail>,
        private modalService: ModalService,
        private translateService: TranslateService,
        private trackingService: TrackingService,
        private sparePartService: SparePartService,
        private wishlistService: WishlistService,
        private breakpointObserver: BreakpointObserver,
        private changeDetector: ChangeDetectorRef,
        private headerconfigService: HeaderConfigService,
        private sanitizer: DomSanitizer,
        private iconRegistry: MatIconRegistry
    ) {
        super();
        this.iconRegistry.addSvgIcon(
            this.matIconName,
            this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/light-bulb.svg')
        );

        this.iconRegistry.addSvgIcon(
            this.matIconName2,
            this.sanitizer.bypassSecurityTrustResourceUrl('assets/icons/light-bulb-white.svg')
        );
    }

    public trackById: TrackByFunction<PartInput> = (_, row: PartInput) =>
        isGroupHeaderRow(row)
            ? row.rowData.title
            : isExpandableRow(row) ||
                isSelectableRow(row) ||
                isCategoryItemRow(row) ||
                isFullRowContent(row) ||
                isContainedRow(row) ||
                isStandardRow(row)
              ? row.rowData.id
              : row.id;

    public ngOnInit(): void {
        const currentUrl = encodeURIComponent(this.window.location.pathname + this.window.location.search);
        this.loginUrl = `${environment.baseURLs.api.domain}/os/c/main.do?viewset=LOGIN&onsuccess=${currentUrl}`;

        this.overlayService.setOverlayContent(PartDetailComponent);

        if (this.isCabinetInverter) {
            this.partDetailService.type = DetailType.DESCRIPTION;
        } else {
            this.partDetailService.type = DetailType.CROSS_SELLING;
        }

        this.partDetailService.getCheckRowsByRecommendation
            .pipe(takeUntil(this.destroySubject))
            .subscribe((value) => {
                if (value) {
                    this.selectRows(value.group, value.rows);
                }
            });

        this.partDetailService.getCheckedRows.pipe(takeUntil(this.destroySubject)).subscribe((value) => {
            if (value) {
                this.checkedRows = value;
            }
        });

        this.partDetailService.getDisplayedItem$
            .pipe(takeUntil(this.destroySubject))
            .subscribe((displayedItem) => {
                this.selectedRowId = displayedItem?.rowData?.id ?? '';
                this.isRecommendationActive = displayedItem !== null;
                this.changeDetector.markForCheck();
            });

        this.filterService.groupIdentifier = (group, allTab) => group?.rowData.title ?? allTab;
        this.sortService.groupIdentifier = (group, allTab) => group?.rowData.title ?? allTab;
        this.breakpointObserver
            .observe([mediaQueries.mq2, mediaQueries.mq3, mediaQueries.mq35, mediaQueries.mq4])
            .pipe(takeUntil(this.destroySubject))
            .subscribe((state: BreakpointState) => {
                let reductionLevel = ReductionLevel.NONE;

                this.isMobile = false;
                if (state.breakpoints[mediaQueries.mq4]) {
                    reductionLevel = ReductionLevel.NONE;
                } else if (state.breakpoints[mediaQueries.mq35]) {
                    reductionLevel = ReductionLevel.FIRST;
                } else if (state.breakpoints[mediaQueries.mq3]) {
                    reductionLevel = ReductionLevel.FIRST;
                    this.isMobile = true;
                } else if (state.breakpoints[mediaQueries.mq2]) {
                    reductionLevel = ReductionLevel.SECOND;
                    this.isMobile = true;
                } else {
                    reductionLevel = ReductionLevel.THIRD;
                    this.isMobile = true;
                }

                this.updateContentReductionLevel(reductionLevel);
            });
    }

    public updateContentReductionLevel(reductionLevel: ReductionLevel) {
        this.contentReductionLevel = reductionLevel;
        this.columns = removeSpecificColumnsIfSEWParterSite(
            this.headerconfigService.getCountryShort(),
            this.getDisplayColumns(this.isGroupList, this.listItems)
        );
        this.changeDetector.detectChanges();
    }

    public ngOnDestroy() {
        this.overlayService.clearOverlayPortal();
        this.overlayService.close();
        this.partDetailService.resetState();
        super.ngOnDestroy();
    }

    public downloadSelectedItemsAsCsvFile() {
        this.trackingService.trackEvent(tracking.step.parts.exportListAsCsv);

        type CsvRow = {
            [key: string]: string | number | undefined;
        };

        const showColumnWear = this.checkedRows.some((item) => item.rowData.wear === true);

        const exportedRows = this.checkedRows.map((item) => {
            const value: CsvRow = {};

            if (isContainedRow(item) && item.groupRow?.rowData?.title && item.groupRow.rowData.title !== '') {
                value[
                    this.translateService.instant('ACTION.EXPORT_EXCEL.COLUMN.DRIVE_GROUP') as keyof CsvRow
                ] = item.groupRow.rowData.title;
            }
            value[this.translateService.instant('SPARE_PARTS.TABLE.INDEX_NO') as keyof CsvRow] =
                item.rowData.itemNumber;
            value[this.translateService.instant('SPARE_PARTS.TABLE.PART_NAME') as keyof CsvRow] =
                this.userStatus?.isUserEmployee && this.exportCSVType === CSVExportType.EXTERNAL
                    ? item.rowData.nameExternal
                    : item.rowData.name;
            value[this.translateService.instant('SPARE_PARTS.TABLE.MAT_NO') as keyof CsvRow] =
                item.rowData.id;
            if (showColumnWear) {
                value[this.translateService.instant('SPARE_PARTS.TABLE.WEAR') as keyof CsvRow] = item.rowData
                    .wear
                    ? this.translateService.instant('YES')
                    : this.translateService.instant('NO');
            }
            value[this.translateService.instant('SPARE_PARTS.TABLE.AVAILABILITY') as keyof CsvRow] =
                `=${this.translateService.instant('ACTION.EXPORT_EXCEL.EXCEL_HYPERLINK_FUNCTION')}("${
                    this.window.location.href.split('?')[0]
                }?SEARCH_STRING=${item.rowData.id}";"${this.translateService.instant(
                    'ACTION.EXPORT_EXCEL.AVAILABILITY_LINK'
                )}")`;
            value[this.translateService.instant('SPARE_PARTS.TABLE.AMOUNT') as keyof CsvRow] =
                this.partAmounts[(item as any).uniqueId];

            return value;
        });

        let csvString = transformObjectsToCsv(exportedRows);
        if (this.userStatus?.isUserEmployee && this.exportCSVType === CSVExportType.INTERNAL) {
            csvString =
                this.translateService.instant('ACTION.EXPORT_EXCEL.INTERNAL_DATA_WARNING') +
                ';'.repeat(Object.keys(exportedRows[0]).length - 1) +
                '\n' +
                csvString;
        }

        downloadCsvFile(
            csvString,
            `${this.translateService.instant('ACTION.EXPORT_EXCEL.FILENAME')}${
                this.searchTerm ? ` ${this.searchTerm.substring(0, 50)}` : ''
            }`
        );
    }

    public onGroupSelect(selectedGroup?: PartGroupRow) {
        this.filterService.updateGroupFilter(selectedGroup);
        this.sortService.updateGroupSorter(selectedGroup);
    }

    public onRowSelect(selectedRows: PartInput[]): void {
        /**
         * #20240611-0442 selected rows not exportable due to changes in the base table rows:
         * PartGroupRow are now selectable. Previously only part rows were selectable...
         */
        const selectedSpareParts = selectedRows.filter((row) => hasRowData(row) && isSparePart(row.rowData));
        if (isSelectableSparePartRowArray(selectedSpareParts)) {
            this.partDetailService.checkedRows = selectedSpareParts;
        }
    }

    public onRowMarked(selectableRow: SelectableRow<SparePart> | null): void {
        const shouldClear = selectableRow === null || selectableRow.rowData.id === this.selectedRowId;
        const nextRow = shouldClear ? null : selectableRow;

        this.selectedRowId = nextRow?.rowData.id ?? '';
        this.isRecommendationActive = nextRow !== null;
        this.partDetailService.displayedItem = nextRow;
        this.rowMarked.emit(nextRow);
    }

    public onShowInDrawing(): void {
        this.selectedRowId = '';
        this.isRecommendationActive = false;
        this.partDetailService.displayedItem = null;
        this.rowMarked.emit(null);
        this.showInDrawing.emit();
    }

    public addToWishList(selectedRows: PartRow[]): void {
        const wishlistButton = this.wishlistButtonRef?.nativeElement;
        const rows = selectedRows.filter(
            (row): row is SelectableRow<SparePart> =>
                (isSelectableRow(row) || isExpandableRow(row)) &&
                hasRowData(row) &&
                isSparePart(row.rowData) &&
                !isCrossSellingItem(row)
        );

        if (!wishlistButton || !this.userStatus?.isUserLoggedIn || !rows.length) {
            return;
        }

        startLoading(wishlistButton);
        const requests = rows.map((row) =>
            this.wishlistService.addItem(this.createWishlistPartRequest(row)).pipe(take(1))
        );

        forkJoin(requests).subscribe({
            next: () => {
                endLoading(wishlistButton);
                this.wishlistService.showHeaderNotification(rows.length);
                this.unselectAll();
            },
            error: () => {
                endLoading(wishlistButton);
                this.wishlistService.showHeaderErrorNotification();
            },
        });
    }

    public addGroupToCart(groupRow: PartGroupRow): void {
        const basketButton = this.buttonRef?.nativeElement;
        const group = groupRow.rowData;

        if (!group.driveGroup) {
            return;
        }
        startLoading(basketButton);

        this.basketService.addProductsToBasket(
            {
                searchterm: this.searchTerm ?? '',
                items: [
                    {
                        type: BasketItemType.DRIVE_GROUP,
                        productId: this.identificationNumber ?? '',
                        driveGroup: group.driveGroup,
                        // zero will return a backend error
                        driveGroupNumber: group.driveGroupNumber ?? 0,
                        quantity: 1,
                    },
                ],
            },
            () => {
                endLoading(basketButton);
            }
        );
    }

    public addSelectionToCart(selectedRows: PartRow[]): void {
        const basketButton = this.basketButtonRef?.nativeElement;

        startLoading(basketButton);
        const rowsWithoutXSellingItems = selectedRows.filter(
            (row) => (isSelectableRow(row) || isExpandableRow(row)) && !isCrossSellingItem(row)
        );

        const gearOilParts = this.extractXSellingPartsByFilter(selectedRows, isGearOilCrossSellingItemRow);
        const eCoverParts = this.extractXSellingPartsByFilter(selectedRows, isECoverCrossSellingItemRow);
        const cableParts = this.extractXSellingPartsByFilter(selectedRows, isCableCrossSellingItemRow);

        // #444692 if APLOUT contains CO_ANTRIEBSPAKET=="N2", the eCover does not need to be configured
        // default is painted then
        const eCoverPartsAlreadyConfigured = eCoverParts.filter(
            (eCover) => !eCoverNeedsConfiguration(eCover)
        );
        const eCoverPartsThatNeedConfiguration = eCoverParts.filter(eCoverNeedsConfiguration);

        const steps = this.addWizardSteps(gearOilParts, eCoverPartsThatNeedConfiguration, cableParts);

        if (this.isXSellingWizardRequired(steps)) {
            this.modalService
                .openDialog({
                    component: PartConfigurationWizardComponent,
                    configuration: {
                        type: '',
                        headline: this.identificationNumber
                            ? `${this.translateService.instant('CONFIGURATION.WIZARD.TITLE')} (${
                                  this.identificationNumber
                              })`
                            : this.translateService.instant('CONFIGURATION.WIZARD.TITLE'),
                        label: '',
                        open: true,
                        size: 'l',
                        status: '',
                        closeButton: true,
                    },
                    data: {
                        isLoggedIn: this.userStatus?.isUserLoggedIn ?? false,
                        steps,
                    },
                })
                .afterClosed$.subscribe((output) => {
                    if (this.isWizardClosedWithoutAction(output)) {
                        this.trackingService.trackEvent(tracking.step.wizard.close);
                        endLoading(basketButton);
                    } else {
                        if (output.data && output.data.length > 0) {
                            const crossSellingItems = output.data;
                            this.addItemsToBasket(
                                this.createBasketItemsFromTableRows(rowsWithoutXSellingItems).concat(
                                    crossSellingItems
                                ),
                                basketButton
                            );
                        }
                    }
                });
        } else {
            this.addItemsToBasket(
                this.createBasketItemsFromTableRows(rowsWithoutXSellingItems).concat(
                    this.createBasketItemsForECovers(eCoverPartsAlreadyConfigured)
                ),
                basketButton
            );
        }
    }

    public openDriveConfigurator(sparePart: SparePart): void {
        const redirectUrl =
            sparePart.availabilityCode === '09'
                ? `${environment.baseURLs.api.domain}/os/cs/?part_number=${encodeURIComponent(sparePart.id)}`
                : `${environment.baseURLs.api.domain}/os/catalog/dispatcher.aspx?action=autosearch&catalog=${sparePart.id}&country=DE&language=en_US`;

        this.window.open(redirectUrl, '_blank')?.focus();
    }

    public onRequestRows(): void {
        console.log('request rows');
    }

    public onTableSearchTermChange(value: string): void {
        this.tableSearchTerm = value;

        this.applyGlobalSearchFilters(value);
    }

    public unselectAll(): void {
        this.table.unselectAllChecked();
    }

    public hideAddDriveGroupToBasket(driveGroup: string): boolean {
        return this.isAtex && driveGroup === 'G';
    }

    public showPartsRecommendation(row: SelectableRow<SparePart> | null): boolean {
        return (row?.rowData?.recommendation?.recommendedParts?.length ?? 0) > 0;
    }

    private addItemsToBasket(basketItems: BasketItem[], basketButton: HTMLSewButtonElement) {
        basketItems = basketItems.map((item) => ({ ...item, isPartOfIndustrialGear: this.isIndustrialGear }));
        const products: Basket = {
            searchterm: this.searchTerm ?? '',
            items: basketItems,
        };
        this.basketService.addProductsToBasket(products, () => {
            endLoading(basketButton);
            this.unselectAll();
        });
    }

    private applyGlobalSearchFilters(value: string): void {
        this.filterService.setMultiPropertyTextFilter(
            GLOBAL_SEARCH_FIELDS,
            GLOBAL_SEARCH_LABEL,
            value,
            GLOBAL_SEARCH_TAG_POSITION,
            false
        );
    }

    private createWishlistPartRequest(row: SelectableRow<SparePart>): WishlistRequest {
        const part = row.rowData;

        const request: WishlistRequest = {
            searchTerm: this.searchTerm ?? '',
            item: {
                type: 'SPARE_PART',
                productId: part.id,
                quantity: Math.abs(this.partAmounts[(row as any).uniqueId] ?? 1),
                isPartOfIndustrialGear: this.isIndustrialGear,
            },
        };

        return request;
    }

    private extractXSellingPartsByFilter(
        selectedRows: PartRow[],
        filter: (obj: InputRow<SparePart, any, any>) => obj is CategoryItemRow<SparePart>
    ) {
        return selectedRows.reduce((xSellingRows, currentRow) => {
            if ((isSelectableRow(currentRow) || isExpandableRow(currentRow)) && filter(currentRow)) {
                return [...xSellingRows, { ...currentRow.rowData, uniqueId: (currentRow as any).uniqueId }];
            }

            return xSellingRows;
        }, [] as SpareParts);
    }

    private addWizardSteps(gearOilParts: SpareParts, eCoverParts: SpareParts, cableParts: SpareParts) {
        const steps: WizardStep<DialogComponent>[] = [];
        if (gearOilParts.length) {
            const groups = this.partGroups?.map(
                ({ rowData: { title, driveGroupProductName, driveGroup, iconName } }) => ({
                    title,
                    name: driveGroupProductName ?? '',
                    type: driveGroup ?? '',
                    iconName,
                })
            );
            steps.push({
                component: GearOilConfigurationComponent,
                data: {
                    title: this.translateService.instant('X_SELLING_DIALOG.GEAR_OIL.TAB_TITLE'),
                    stepData: {
                        type: StepType.GEAR_OIL,
                        parts: gearOilParts.map((part) => {
                            const group = this.partGroups?.find(
                                (currentGroup) =>
                                    currentGroup.rowData.title === (part as any).uniqueId.split('_')[0]
                            );

                            return {
                                ...part,
                                uniqueId: group ? `${group?.rowData.title}_${part.id}` : part.id,
                                isIndustrialGear: this.isIndustrialGear,
                            };
                        }),
                        isLoggedIn: this.userStatus?.isUserLoggedIn ?? false,
                        items: this.sparePartService.getCrossSellingGearOil(gearOilParts, groups),
                    },
                    stepTrackingPoint: tracking.step.wizard.step.gearOil,
                },
                finished: false,
            });
        }

        if (eCoverParts.length) {
            steps.push({
                component: ECoverConfigurationComponent,
                data: {
                    title: this.translateService.instant('X_SELLING_DIALOG.E_COVER.TAB_TITLE'),
                    stepData: {
                        type: StepType.E_COVER,
                        eCoverParts,
                        partAmounts: this.partAmounts,
                        serialNumber: this.identificationNumber,
                        isDtcKit: this.isDtcKit,
                    },
                    stepTrackingPoint: tracking.step.wizard.step.eCover,
                },
                finished: false,
            });
        }

        if (cableParts.length) {
            steps.push({
                component: CableConfigurationComponent,
                data: {
                    title: this.translateService.instant('X_SELLING_DIALOG.CABLE.TAB_TITLE'),
                    stepData: {
                        type: StepType.CABLE,
                        parts: cableParts,
                        partAmounts: this.partAmounts,
                    },
                    stepTrackingPoint: tracking.step.wizard.step.cable,
                },
                finished: false,
            });
        }

        return steps;
    }

    private createBasketItemsFromTableRows(rows: PartRow[]) {
        return rows.map((row) => {
            let item;

            if (isGroupHeaderRow(row)) {
                item = (row as PartGroupRow).rowData;

                return {
                    type: BasketItemType.DRIVE_GROUP,
                    productId: this.identificationNumber ?? '',
                    driveGroup: item.driveGroup,
                    // zero will return a backend error
                    driveGroupNumber: item.driveGroupNumber ?? 0,
                    quantity: 1,
                } as BasketItem;
            }

            item = (row as SpecialRow<SparePart>).rowData;

            return {
                type: BasketItemType.SPARE_PART,
                productId: item.id,
                quantity: Math.abs(this.partAmounts[(row as any).uniqueId] ?? 1),
            } as BasketItem;
        });
    }

    private createBasketItemsForECovers(rows: SpareParts) {
        return rows.map(
            (row) =>
                ({
                    type: BasketItemType.E_COVER,
                    productId: row.id,
                    isDtcKit: this.isDtcKit,
                    // #444692 if APLOUT contains CO_ANTRIEBSPAKET=="N2", the eCover needs to be painted by default
                    isPainted: true,
                    serialNumber: this.identificationNumber,
                    quantity: Math.abs(this.partAmounts[(row as any).uniqueId] ?? 1),
                }) as BasketItem
        );
    }

    private selectRows(group: string, rows: SelectableRow<RecommendedPart>[]) {
        rows.forEach((selectedRow) => {
            this.partGroups
                ?.find((partGroupRow) => partGroupRow.rowData.title === group)
                ?.containedRows.forEach((containedRow) => {
                    if (
                        (containedRow as PartRow & { rowData: { id: string } }).rowData.id ===
                            selectedRow.rowData.id &&
                        (containedRow as PartRow & { rowData: { itemNumber: string } }).rowData.itemNumber ===
                            selectedRow.rowData.itemNumber
                    ) {
                        this.table.toggleRowChecked(containedRow, true);
                        this.updatePartAmount(
                            (containedRow as PartRow & { uniqueId: string }).uniqueId,
                            selectedRow.rowData.installedQuantity
                        );
                    }
                });
        });
    }

    private initializeGroupRow(group: SparePartGroup): PartGroupRow {
        const groupRow = {} as PartGroupRow;

        const initializeExpandablePartRows = (parts: SpareParts): PartRow[] =>
            parts.map((part, index) => ({
                uniqueId: getUniquePartId(group, part, index),
                isExpandableRow: true,
                isSelectableRow: true,
                isContainedRow: true,
                isCategoryItemRow: this.collapsableGroupHeaders,
                isDisabled: part.availabilityCode === '01' || part.availabilityCode === '05',
                rowData: {
                    ...part,
                    itemNumber: getItemNumber(part, index),
                },
                expandData: {
                    ...part,
                },
                groupRow: {
                    rowData: {
                        /**
                         * Prevent a cyclic structure by limiting the `rowData` of `groupRow` to just the identifier and title.
                         * Previously the whole object of the groupRow was included and led to the unit tests error message:
                         * ```bash
                         * TypeError: Converting circular structure to JSON
                         * --> starting at object with constructor 'Object'
                         * |     property 'containedRows' -> object with constructor 'Array'
                         * |     index 0 -> object with constructor 'Object'
                         * --- property 'groupRow' closes the circle
                         * at stringify (<anonymous>)
                         * ```
                         **/
                        uniqueGroupId: getUniqueGroupId(groupRow.rowData),
                        title: groupRow.rowData.title,
                    },
                },
            }));

        groupRow.isGroupHeaderRow = true;
        groupRow.isSelectableRow = this.collapsableGroupHeaders ? false : true;
        groupRow.isDisabled = group.disableAddGroupToCart ?? false;
        groupRow.rowData = group;

        groupRow.containedRows = initializeExpandablePartRows(group.spareParts ?? []);

        if (this.collapsableGroupHeaders) {
            (groupRow as CategoryHeaderRow<SparePartGroup, PartRow>).isCategoryHeaderRow = true;
        }

        return groupRow;
    }

    private updateGroupRows(groups: SparePartGroups, compareByIdAndIndex: boolean): PartGroupRow[] {
        this.previousPartGroupRows.forEach((prevGroup) => {
            const group = groups.find((groupEl) => prevGroup.rowData.title === groupEl.title);

            // Disable test coverage as this case is not reproducable due to a previous method call `areRowsEqual`
            /* istanbul ignore next */
            if (!group) {
                return;
            }

            prevGroup.containedRows.filter(isPartRowIdAndItemNumber).forEach((prevContainedRow) => {
                // Disable test coverage as this case is not reproducable due to a previous method call `areRowsEqual`
                /* istanbul ignore next */
                const sparePart = group.spareParts?.find((sparePartEl) => {
                    if (compareByIdAndIndex) {
                        // identify unique spare part of e.g. serialNumberResult
                        return (
                            sparePartEl.itemNumber === prevContainedRow.rowData.itemNumber &&
                            sparePartEl.id === prevContainedRow.rowData.id
                        );
                    } else {
                        // identify unique spare part of e.g. sparePartResult
                        return sparePartEl.id === prevContainedRow.rowData.id;
                    }
                });

                // update the properties inside the spareParts without loosing their reference,
                // which is used e.g. for comparing the selected lines (otherwise the selected line would be lost on an upddate)
                if (sparePart) {
                    Object.assign(prevContainedRow.rowData, prevContainedRow.rowData, sparePart);
                    Object.assign(prevContainedRow.expandData, prevContainedRow.expandData, sparePart);
                }
            });
            // update the properties inside the drive group row, as reference is not important here:
            // *.rowData === * is not used... (only possible because the drive group line is not selectable)
            // Object.assign has thrown error: https://stackoverflow.com/a/58279719
            prevGroup.rowData = { ...prevGroup.rowData, ...group };
        });

        return this.previousPartGroupRows;
    }

    /**
     * Returns the rows which will be displayed.
     *
     * Some features, for example the filtering or simple comparisons inside the base-table,
     * are using the rows `reference/ pointer` to compare it to other rows.
     * Any asynchronously loaded content therefore has to be integrated in the previous rows to
     * further be able to compare by reference. The usage of map or destructuring for exmample
     * would break this mechanism.
     *
     * @see issue #446175
     *
     * @param groups spare part groups
     * @param isUpdate false, if initial creation, else true
     * @param compareByIdAndIndex wheter the comparison should be done by id and index (e.g. serialNumberResults) or just by id (e.g. sparePartResult)
     * @returns group rows to display in the base table
     */
    private createGroupRows(
        groups: SparePartGroups,
        isUpdate: boolean,
        compareByIdAndIndex: boolean
    ): PartGroupRow[] {
        let result;

        if (isUpdate) {
            result = this.updateGroupRows(groups, compareByIdAndIndex);
            this.changeDetector.detectChanges();
        } else {
            result = groups.reduce(
                (rows, group) => [...rows, this.initializeGroupRow(group)],
                [] as PartGroupRow[]
            );
        }
        this.previousGroups = groups;
        this.previousPartGroupRows = result;

        return result;
    }

    private setupPartAmounts(groups: SparePartGroups): PartAmounts {
        return groups.reduce(
            (amounts, group) => ({
                ...amounts,
                ...group.spareParts?.reduce((groupAmounts, part, index) => {
                    const uniqueId = getUniquePartId(group, part, index);

                    return {
                        ...groupAmounts,
                        [uniqueId]: this.partAmounts[uniqueId]
                            ? this.partAmounts[uniqueId]
                            : (part.installedQuantity ?? 1),
                    };
                }, {}),
            }),
            {}
        );
    }

    private updatePartAmount(uniqueId: string, amount: number) {
        this.partAmounts[uniqueId] = amount;
    }

    private displayColumnWear(groups: SparePartGroups): boolean {
        // determines whether at least one spare part is a wear part
        return groups
            .reduce(
                (parts, group) => (group.spareParts ? [...parts, ...group.spareParts] : [...parts, ...[]]),
                [] as SpareParts
            )
            .some((entry) => entry.wear);
    }

    private getDisplayColumns(isGroupList: boolean, groups: SparePartGroups): string[] {
        const contentLevel = this.contentReductionLevel;

        if (isGroupList) {
            const displayWear = this.displayColumnWear(groups);

            return [
                ...(this.collapsableGroupHeaders ? [] : ['itemNumber']),
                'name',
                ...(contentLevel < ReductionLevel.SECOND ? ['materialNumber'] : []),
                ...(contentLevel < ReductionLevel.SECOND && displayWear ? ['wear'] : []),
                ...(contentLevel < ReductionLevel.THIRD ? ['availability'] : []),
                'amount',
                ...(contentLevel < ReductionLevel.THIRD ? ['addToBasket'] : []),
            ];
        }

        return [
            'index',
            'name',
            ...(contentLevel < ReductionLevel.SECOND ? ['materialNumber'] : []),
            ...(contentLevel < ReductionLevel.THIRD ? ['availability'] : []),
            'amount',
            ...(contentLevel < ReductionLevel.THIRD ? ['addToBasket'] : []),
        ];
    }

    private getDisplayGroupHeaderColumns(): string[] | null {
        if (this.isSparePartListResult(this.listItems)) {
            return null;
        }

        return ['groupHeaderIcon', 'groupHeaderName', 'groupHeaderAddToBasket'];
    }

    private createDummyGroupList(spareParts: SpareParts): SparePartGroups {
        return [{ title: '', driveGroup: '', spareParts }];
    }

    private isXSellingWizardRequired(steps: WizardStep<DialogComponent>[]) {
        return steps.length > 0;
    }

    private isWizardClosedWithoutAction(output: XSellingWizardOutput | undefined): output is undefined {
        return !output?.data;
    }

    private isSparePartListResult(sparePartGroups: SparePartGroups) {
        return sparePartGroups.every((group) => group.driveGroup === undefined);
    }

    /**
     * Checks if the old rows are also contained in the new rows.
     * In detail it checks if the the old group rows and their spare parts
     * are contained in the new rows, independent of their specific content.
     *
     * @param rows
     * @param newRows
     * @returns `true` if they contain the same rows, else `false`
     */
    private areGroupRowsAndSparePartsConsistent(rows: SparePartGroups, newRows: SparePartGroups) {
        return (
            rows.length === newRows.length &&
            rows.every((row) =>
                newRows.some((newRow) => {
                    if (row.title === newRow.title) {
                        if (
                            row.driveGroup === newRow.driveGroup &&
                            row.spareParts === undefined &&
                            newRow.spareParts === undefined
                        ) {
                            return true;
                        } else if (
                            row.driveGroup === newRow.driveGroup &&
                            row.spareParts &&
                            newRow.spareParts &&
                            newRow.spareParts.length === newRow.spareParts.length
                        ) {
                            return row.spareParts.some((sparePart) =>
                                /* istanbul ignore next */
                                newRow.spareParts?.find(
                                    (newSparePart) =>
                                        newSparePart.itemNumber === sparePart.itemNumber &&
                                        newSparePart.id === sparePart.id
                                )
                            );
                        } else {
                            return false;
                        }
                    } else {
                        return false;
                    }
                })
            )
        );
    }
}
